import React, { useCallback, useEffect, useRef, useState } from 'react';
import './index.scss';
import { apiHolder, apis, ProductTagEnum, api, getIn18Text } from 'api';
import debounce from 'lodash/debounce';
import { Select, Spin } from 'antd';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import Avatar from '@web-common/components/UI/Avatar';
import ProductAuthTag from '@web-common/components/UI/ProductAuthTag/ProductAuthTag';
import { buildContactModel } from '@web-common/utils/contact_util';
import { useAppDispatch, useAppSelector } from '@web-common/state/createStore';
const loadingIcon = React.createElement(LoadingOutlined, { style: { fontSize: 24 }, spin: true });
const contactApi = apiHolder.api.requireLogicalApi(apis.contactApiImpl);
const htmlApi = apiHolder.api.requireLogicalApi(apis.htmlApi);
const systemApi = api.getSystemApi();
export const MailAt = ({ visible, setVisible, emitResult, resultPos }) => {
    const dispath = useAppDispatch();
    const curAccount = useAppSelector(state => { var _a; return (_a = state.mailReducer.currentMail) === null || _a === void 0 ? void 0 : _a.optSenderMainEmail; });
    const [keyWords, setKeyWords] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [inProcess, setInProcess] = useState(false);
    const [fetching, setFetching] = useState(false);
    const containerRef = useRef(null);
    const doSearch = useCallback((words) => {
        contactApi.doSearchNew({
            query: words,
            showDisable: false,
            useEdmData: process.env.BUILD_ISEDM,
            exclude: ['orgName', 'orgPYName'],
            _account: curAccount,
        }, res => {
            var _a, _b;
            setFetching(false);
            const _curAccount = curAccount || ((_a = systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '';
            const mainRes = ((_b = res.main[_curAccount]) === null || _b === void 0 ? void 0 : _b.contactList) || [];
            const highlight = (target) => {
                target = htmlApi.encodeHtml(target);
                return target.replace(new RegExp(`(${words})`, 'g'), '<span class="at-result-highlight">$1</span>');
            };
            const results = mainRes.reduce((total, current) => {
                const email = current.hitQueryEmail || current.accountName || '';
                const baseResult = {
                    contactId: current.id,
                    accountId: current.id,
                    avatar: current.avatar || '',
                    contactName: current.contactName,
                    unescapedContactName: current.contactName,
                    accountName: email ? `&lt;${htmlApi.encodeHtml(email)}&gt;` : '',
                    unescapedAccountName: email,
                    orgName: Array.isArray(current.position) && current.position.length > 0 ? current.position.slice(0, 2).map(v => v.join('/')) : [],
                    contact: { contactName: current.contactName, avatar: current.avatar || '', accountName: email, type: current.type || 'enterprise', id: current.id },
                };
                if (current.hitQuery && current.hitQuery.includes('contactName')) {
                    baseResult.contactName = highlight(baseResult.contactName);
                }
                if (current.hitQuery && current.hitQuery.includes('accountName') && email) {
                    baseResult.accountName = `&lt;${highlight(email)}&gt;`;
                }
                return [...total, baseResult];
            }, []);
            setSearchResults(results);
        });
    }, [keyWords, curAccount]);
    const reset = useCallback(() => {
        setKeyWords('');
        setSearchResults([]);
        setVisible(false);
    }, [setKeyWords, setSearchResults, setVisible]);
    const searchHandler = useCallback((words) => {
        if (!inProcess || !words) {
            setSearchResults([]);
            setFetching(false);
            return;
        }
        doSearch(words);
    }, [inProcess, setSearchResults, setFetching, doSearch]);
    const searchContactDebounce = useCallback(debounce(searchHandler, 300), [inProcess]);
    const onResultClick = useCallback((item) => {
        var _a;
        if (typeof item === 'string') {
            emitResult(item);
        }
        else {
            const contactItem = {
                name: item.unescapedContactName,
                email: item.unescapedAccountName,
                type: ((_a = item.contact) === null || _a === void 0 ? void 0 : _a.type) || 'enterprise',
                avatar: item.avatar,
                id: item.contactId,
            };
            const contactModel = buildContactModel(contactItem);
            emitResult(contactModel);
        }
        setInProcess(false);
    }, []);
    const onKeywordsChange = useCallback(value => {
        setKeyWords(value);
    }, [setKeyWords]);
    const onSelect = useCallback(index => {
        const target = searchResults[index];
        onResultClick(target);
    }, [searchResults, onResultClick]);
    const onKeydown = useCallback(e => {
        if (e.keyCode === 27) {
            reset();
        }
        if (e.keyCode === 13) {
            if (searchResults.length === 0) {
                onResultClick(keyWords);
            }
        }
    }, [searchResults, keyWords]);
    useEffect(() => {
        setInProcess(visible);
    }, [visible]);
    useEffect(() => {
        setFetching(!!keyWords);
        setSearchResults([]);
        searchContactDebounce(keyWords);
    }, [keyWords]);
    useEffect(() => {
        if (!inProcess) {
            reset();
        }
    }, [inProcess]);
    if (!inProcess) {
        return React.createElement(React.Fragment, null);
    }
    const searchEmptyContent = keyWords ? React.createElement("p", null, getIn18Text('WUCILIANXIREN')) : null;
    const notFoundContent = fetching ? React.createElement(Spin, { indicator: loadingIcon }) : searchEmptyContent;
    const searchOptions = searchResults.map((v, index) => (React.createElement(Select.Option, { key: v.contactId + (v.id || ''), value: index },
        React.createElement("div", { className: "mail-at-search-result-item" },
            React.createElement(Avatar, { item: { contact: v.contact }, size: 28, style: {
                    flexGrow: 0,
                    flexShrink: 0,
                    marginRight: '12px',
                } }),
            React.createElement("div", { className: "mail-at-contact-info" },
                React.createElement("p", { className: "mail-at-contact-info-top", title: v.unescapedAccountName },
                    React.createElement("span", { dangerouslySetInnerHTML: { __html: v.contactName } }),
                    React.createElement("span", { dangerouslySetInnerHTML: { __html: v.accountName } })),
                v.orgName.map((org, orgIndex) => (React.createElement("p", { key: org + orgIndex, className: "mail-at-contact-info-bottom", title: org }, org))))))));
    return (React.createElement("div", { className: "mail-at-container" },
        React.createElement(ProductAuthTag, { tagName: ProductTagEnum.AT_CONTACT },
            React.createElement(Select, { autoFocus: true, showSearch: true, style: { width: '290px' }, value: keyWords || undefined, placeholder: getIn18Text('QINGSHURULIANXI'), defaultActiveFirstOption: true, showArrow: false, filterOption: false, onSearch: onKeywordsChange, onSelect: onSelect, onInputKeyDown: onKeydown, onBlur: () => setInProcess(false), dropdownClassName: "mail-at-search-result", notFoundContent: notFoundContent, getPopupContainer: () => containerRef.current || document.body, listHeight: 246, dropdownRender: menu => (React.createElement("div", null,
                    menu,
                    searchOptions)) }, searchOptions)),
        React.createElement("div", { className: "mail-at-dropdown-container", ref: containerRef, style: resultPos === 'top' ? { top: fetching || searchOptions.length === 0 ? '-46px' : '-260px' } : { top: '36px' } })));
};
